import React, { useCallback, useEffect, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { cx } from '@emotion/css';

import { colors } from '@ajaibid/components';
import { thousandSeparator, isEmpty } from '@ajaibid/utils';
import { OrderBookSocket } from 'Component/Socket';

import {
  styOrderBookSectionContainer,
  styOrderBookColumn,
  styOrderBookItem,
  styOrderBookItemGap,
  styOrderBookRunningContainer,
  styOrderBookRunningHeader,
  styOrderBookRunningItem,
} from './styles';
import useHandleSocketMessage from './useHandleSocketMessage';

const getLotVolume = (lot, totalLotInOrder) => {
  const lotVolume = (lot / totalLotInOrder) * 100;
  return lotVolume;
};

const getHighestLot = (orderData) => {
  if (!orderData || orderData.length === 0) return 0;
  const highestLot = orderData.reduce((acc, cur) => {
    if (acc < cur.lot) return cur.lot;
    return acc;
  }, 0);
  return highestLot;
};

function makeSortPrice(desc = false) {
  return (a, b) => {
    if (a.price === b.price) {
      return 0;
    }

    const sortResult = a.price > b.price ? 1 : -1;

    if (desc) {
      return sortResult * -1;
    }

    return sortResult;
  };
}

function RunningOrderBook({
  code,
  stockPriceData,
  askOrderData,
  bidOrderData,
  onClickRunningOrder,
}) {
  const [bidOrder, setBidOrder] = useState(bidOrderData);
  const [askOrder, setAskOrder] = useState(askOrderData);

  const highestLot = useMemo(() => {
    const combinedOrder = [...bidOrder, ...askOrder];
    const highestOrderLot = getHighestLot(combinedOrder);
    return highestOrderLot;
  }, [bidOrder, askOrder]);

  useEffect(() => {
    setAskOrder(askOrderData);
  }, [askOrderData, setAskOrder]);

  useEffect(() => {
    setBidOrder(bidOrderData);
  }, [bidOrderData, setBidOrder]);

  const handleSocketMessage = useHandleSocketMessage({
    askOrder,
    bidOrder,
    code,
    setAskOrder,
    setBidOrder,
  });

  const askOrderDisplay = useMemo(() => askOrder?.sort(makeSortPrice(false) || []), [askOrder]);
  const bidOrderDisplay = useMemo(() => bidOrder?.sort(makeSortPrice(true) || []), [bidOrder]);

  if (!stockPriceData) return null;

  const openPrice = stockPriceData?.open || 0;
  const priceIndicator = (price) => {
    switch (true) {
      case price > openPrice:
        return colors.GREEN_5;
      case price < openPrice:
        return colors.RED_5;
      default:
        return '';
    }
  };

  return (
    <>
      <OrderBookSocket code={code} onMessage={handleSocketMessage} />
      <div className={cx(styOrderBookSectionContainer, styOrderBookRunningContainer)}>
        <div className={cx(styOrderBookColumn, styOrderBookItemGap)}>
          <div className={cx(styOrderBookItem, styOrderBookRunningHeader)}>
            <span>Lot</span>
            <span>Bid</span>
          </div>
          {bidOrderDisplay.map((item, index) => (
            <div
              key={`BID-${index}`}
              className={cx(
                styOrderBookItem,
                styOrderBookRunningItem(
                  priceIndicator(item.price),
                  'right',
                  getLotVolume(item.lot, highestLot)
                )
              )}
              onClick={() => onClickRunningOrder(item)}
            >
              <span className="item-lot">{thousandSeparator(item.lot)}</span>
              <span className="item-price">{thousandSeparator(item.price)}</span>
            </div>
          ))}
        </div>
        <div className={cx(styOrderBookColumn, styOrderBookItemGap)}>
          <div className={cx(styOrderBookItem, styOrderBookRunningHeader)}>
            <span>Ask</span>
            <span>Lot</span>
          </div>
          {askOrderDisplay.map((item, index) => (
            <div
              key={`ASK-${index}`}
              className={cx(
                styOrderBookItem,
                styOrderBookRunningItem(
                  priceIndicator(item.price),
                  'left',
                  getLotVolume(item.lot, highestLot)
                )
              )}
              onClick={() => onClickRunningOrder(item)}
            >
              <span className="item-price">{thousandSeparator(item.price)}</span>
              <span className="item-lot">{thousandSeparator(item.lot)}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

RunningOrderBook.propTypes = {
  stockPriceData: propTypes.objectOf(propTypes.any),
  askOrderData: propTypes.arrayOf(propTypes.any),
  bidOrderData: propTypes.arrayOf(propTypes.any),
  code: propTypes.string,
  onClickRunningOrder: propTypes.func,
};

RunningOrderBook.defaultProps = {
  stockPriceData: null,
  askOrderData: [],
  bidOrderData: [],
  code: '',
  onClickRunningOrder: () => {},
};

export default RunningOrderBook;
